import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import {
  ApiErrorDto,
  bffApi,
  Category,
  ContractDataFieldNameDto,
  ContractDataFieldSectionDto,
  ContractPriority,
  ContractRisk,
  CounterpartyInputDto,
  Currency,
  EditOption as EditOptionDto,
  EmailInviteLanguage,
  FieldErrorDto,
  LastSeenOption,
  OptionDto,
  PhoneNumberDto,
  SignerDto,
  UpdateContractRequest,
} from "../../../../autogen/bff-api";
import { onlyUniqueStringsFilter } from "../../../onlyUniqueStringsFilter";
import {
  ContractState,
  FailedContractEdit,
  OPTION_CHANGED_ELSEWHERE,
  VERSION_CONFLICT,
} from "../../reducers/contractReducer";
import { toLastSeen } from "./lastSeenOption";
import { fetchSettledContract } from "./load-contract-thunk";
import { isRefusedAsAlreadyDecided } from "./optionDecidedElsewhere";
import { RootState } from "../../store";

export const contractEditSettled = createAction<ContractState>("contract/editSettled");

export const editContractThunk = createAsyncThunk(
  "contract/edit",
  async (
    props: {
      command: EditContractCommand;
    },
    { dispatch, getState }
  ): Promise<ContractState | null> => {
    const currentState = getState() as RootState;

    const currentContractState = currentState.contract.state;
    if (!currentContractState) {
      throw Error("Should never happen");
    }

    switch (props.command.type) {
      case "EditAwardDate":
      case "EditRenewalDate":
      case "EditCounterpartyContactPerson":
      case "EditDeliveryTerms":
      case "EditEstimatedValue":
      case "EditExpirationDate":
      case "EditExternalLink":
      case "EditInternalReferenceNumber":
      case "EditLinkedContracts":
      case "EditOriginSourcingEvent":
      case "EditPaymentTermsInDays":
      case "EditPricePerMonth":
      case "EditPricePerYear":
      case "EditPriority":
      case "EditPurchasingPolicy":
      case "EditRisk":
      case "EditStartDate":
      case "EditTotalLiability":
      case "EditTotalPrice":
      case "EditWarrantyExpirationDate":
      case "EditTitle":
      case "EditDescription":
      case "EditInternalOwner":
      case "EditTemplate":
      case "EditProjects":
      case "EditCounterparty":
      case "EditTimezone":
      case "EditCurrency":
      case "EditIfrsValues":
      case "AddDataFields":
      case "EditCustomFields":
      case "AddTerminationDate":
      case "EditInternalParties":
      case "EditSupplierInfo":
      case "AddMonetaryAdjustment":
      case "AddPercentageAdjustment":
      case "RemoveMonetaryAdjustment":
      case "RemovePercentageAdjustment":
      case "EditSupplierCategory":
      case "EditNoticePeriod":
      case "EditOption":
      case "RemoveOption":
      case "RemoveDataField":
      case "RemoveDescriptionDocument": {
        type Sent = { saved: boolean; sequence: number; state: ContractState };
        const latestOf = (state: ContractState): ContractState => {
          const latest = (getState() as RootState).contract.state;
          return latest && latest.id === state.id ? latest : state;
        };

        const send = async (command: EditContractCommand, state: ContractState, retried = false): Promise<Sent> => {
          const field = fieldOf(command);
          const sequence = editStarted(field);
          const request = againstSavedByRecheck(command, latestOf(state));
          const update = async () => {
            const response = dispatch(
              bffApi.endpoints.updateContract.initiate({
                contractId: state.id,
                updateContractRequest: getEditRequest(request),
              })
            );
            response.reset();
            return response;
          };
          const result = await update()
            .then((first) => (isMergedAgainOnResend(request, first) ? update() : first))
            .finally(() => editFinished(field));
          const base = latestOf(state);

          if ("data" in result && result.data) {
            const saved = getContractState(command, result.data as ContractState["contract"], base);
            return { saved: true, sequence, state: keepingNewerEditOf(field, sequence, saved, base) };
          }
          const decidedOptionId = optionDecidedElsewhere(command, result);
          if (decidedOptionId) {
            const fresh = await dispatch(fetchSettledContract({ contractId: state.id }))
              .unwrap()
              .catch(() => null);
            const latest = latestOf(state);
            const contract = (fresh as ContractState["contract"] | null) ?? latest.contract;
            return {
              saved: false,
              sequence,
              state: withOptionDecidedElsewhere(command, decidedOptionId, contract, latest),
            };
          }
          if (!retried && conflictsWithARecheck(result, request, base)) return send(command, state, true);
          return { saved: false, sequence, state: keepingNewerEditOf(field, sequence, getErrors(command, base, result), base) };
        };

        const sendConflictedSince = async (field: string, sequence: number, state: ContractState) => {
          const conflicted = (latestOf(state).failedEdits ?? []).find(
            (failed) => fieldOf(failed.command) === field && failed.errorCode === OPTION_CHANGED_ELSEWHERE
          );
          if (!conflicted || !startedAfter(field, sequence) || isInFlight(field)) return;
          dispatch(contractEditSettled((await send(conflicted.command, state, true)).state));
        };

        const edited = await send(props.command, currentContractState);
        if (!edited.saved || !changesWhatContractDatesAreCheckedAgainst(props.command)) return edited.state;
        dispatch(contractEditSettled(edited.state));
        for (const field of heldRecheckableFields(latestOf(edited.state))) {
          const held = heldRefusal(latestOf(edited.state), field);
          if (!held || editedSince(field, edited.sequence)) continue;
          const resent = await send(held.command, edited.state);
          dispatch(contractEditSettled(resent.saved ? withResendSaved(held.command, resent.state) : resent.state));
          if (resent.saved) await sendConflictedSince(field, resent.sequence, edited.state);
        }
        return latestOf(edited.state);
      }
      case "SendDocumentsForSigning": {
        const response = dispatch(
          bffApi.endpoints.createSignedDocument.initiate({
            contractId: currentContractState.id,
            createSignedDocumentRequest: {
              mainDocumentId: props.command.value.mainContractDocumentId,
              signers: props.command.value.signers,
              language: EmailInviteLanguage.No,
            },
          })
        );
        response.reset();
        const result = await response;

        if ("data" in result && result.data) {
          return getContractState(props.command, result.data as ContractState["contract"], currentContractState);
        } else {
          return getErrors(props.command, currentContractState, result);
        }
      }
    }
  }
);

const getContractState = (
  command: EditContractCommand,
  body: ContractState["contract"],
  currentContractState: ContractState
): ContractState => {
  const failedEdits = (currentContractState.failedEdits ?? []).filter(
    (failed) => !isSameEdit(failed.command, command) && !removesOptionOf(command, failed.command)
  );
  return {
    id: currentContractState.id,
    contract: body,
    lastChanged: new Date().toISOString(),
    errors: currentContractState.errors.filter(
      (e) => e !== command.type || failedEdits.some((failed) => failed.command.type === e)
    ),
    failedEdits,
    optionsDecidedElsewhere: currentContractState.optionsDecidedElsewhere,
    refusedRemovals: clearsRefusedRemoval(command) ? [] : currentContractState.refusedRemovals,
    optionsSavedByRecheck: withoutOptionOf(command, currentContractState.optionsSavedByRecheck),
  };
};

const withoutOptionOf = (
  command: EditContractCommand,
  saved: ContractState["optionsSavedByRecheck"]
): ContractState["optionsSavedByRecheck"] => {
  if (command.type !== "EditOption" || !command.option.id || !saved?.[command.option.id]) return saved;
  const rest = { ...saved };
  delete rest[command.option.id];
  return rest;
};

const conflictsWithARecheck = (result: object, sent: EditContractCommand, latest: ContractState): boolean =>
  sent.type === "EditOption" &&
  refusalErrorCode(result) === OPTION_CHANGED_ELSEWHERE &&
  JSON.stringify(againstSavedByRecheck(sent, latest)) !== JSON.stringify(sent);

const keepingNewerEditOf = (
  field: string,
  sequence: number,
  outcome: ContractState,
  latest: ContractState
): ContractState => {
  if (!startedAfter(field, sequence)) return outcome;
  const newer = (latest.failedEdits ?? []).filter((failed) => fieldOf(failed.command) === field);
  return {
    ...outcome,
    failedEdits: [...(outcome.failedEdits ?? []).filter((failed) => fieldOf(failed.command) !== field), ...newer],
    errors: [...outcome.errors, ...newer.map((failed) => failed.command.type)].filter(onlyUniqueStringsFilter),
  };
};

const againstSavedByRecheck = (
  command: EditContractCommand,
  state: ContractState | null | undefined
): EditContractCommand => {
  const saved = command.type === "EditOption" && command.option.id && state?.optionsSavedByRecheck?.[command.option.id];
  return command.type === "EditOption" && saved ? { ...command, lastSeen: toLastSeen(saved) } : command;
};

const withResendSaved = (command: EditContractCommand, state: ContractState): ContractState =>
  command.type === "EditOption" && command.option.id
    ? { ...state, optionsSavedByRecheck: { ...state.optionsSavedByRecheck, [command.option.id]: command.option } }
    : state;

const clearsRefusedRemoval = (command: EditContractCommand): boolean =>
  command.type === "RemoveDataField" || command.type === "EditExpirationDate";

const optionDecidedElsewhere = (command: EditContractCommand, result: object): string | undefined => {
  if (!("error" in result) || !isRefusedAsAlreadyDecided(result.error)) return undefined;
  if (command.type === "EditOption") return command.option.id;
  if (command.type === "RemoveOption") return command.id;
  return undefined;
};

const withOptionDecidedElsewhere = (
  command: EditContractCommand,
  optionId: string,
  fresh: ContractState["contract"],
  currentContractState: ContractState
): ContractState => {
  const state = getContractState(command, fresh, currentContractState);
  return {
    ...state,
    optionsDecidedElsewhere: [...(state.optionsDecidedElsewhere ?? []).filter((id) => id !== optionId), optionId],
  };
};

const getErrors = (command: EditContractCommand, currentContractState: ContractState, result: object): ContractState => {
  const removalFieldErrors = command.type === "RemoveDataField" ? refusalFieldErrors(result) ?? [] : [];
  if (isValueRefused(result) && removalFieldErrors.length > 0) {
    return {
      ...currentContractState,
      refusedRemovals: [
        ...(currentContractState.refusedRemovals ?? []).filter((refused) => !isSameEdit(refused.command, command)),
        { command, fieldErrors: removalFieldErrors },
      ],
    };
  }
  return {
    ...currentContractState,
    errors: [...currentContractState.errors, command.type].filter(onlyUniqueStringsFilter),
    failedEdits: [
      ...(currentContractState.failedEdits ?? []).filter((failed) => !isSameEdit(failed.command, command)),
      ...(isValueEdit(command)
        ? [
            {
              command,
              valueRefused: isValueRefused(result),
              fieldErrors: refusalFieldErrors(result),
              errorCode: refusalErrorCode(result),
            },
          ]
        : []),
    ],
  };
};

const changesWhatContractDatesAreCheckedAgainst = (command: EditContractCommand): boolean =>
  ["EditStartDate", "EditExpirationDate", "EditOption", "RemoveOption"].includes(command.type);

const RECHECKED_WHEN_HELD: EditContractCommandType[] = ["EditStartDate", "EditExpirationDate", "EditOption"];

const fieldOf = (command: EditContractCommand): string =>
  command.type === "EditOption" ? `EditOption:${command.option.id}` : command.type;

const heldRecheckable = (state: ContractState | null | undefined): FailedContractEdit[] =>
  (state?.failedEdits ?? []).filter(
    (failed) =>
      failed.valueRefused &&
      failed.errorCode !== OPTION_CHANGED_ELSEWHERE &&
      RECHECKED_WHEN_HELD.includes(failed.command.type)
  );

const heldRecheckableFields = (state: ContractState | null | undefined): string[] =>
  heldRecheckable(state).map((failed) => fieldOf(failed.command));

const heldRefusal = (state: ContractState | null | undefined, field: string): FailedContractEdit | undefined =>
  heldRecheckable(state).find((failed) => fieldOf(failed.command) === field);

let editSequence = 0;
const lastEditStarted = new Map<string, number>();
const editsInFlight = new Map<string, number>();

const editStarted = (field: string): number => {
  editSequence += 1;
  lastEditStarted.set(field, editSequence);
  editsInFlight.set(field, (editsInFlight.get(field) ?? 0) + 1);
  return editSequence;
};

const editFinished = (field: string) => editsInFlight.set(field, (editsInFlight.get(field) ?? 1) - 1);

const startedAfter = (field: string, sequence: number): boolean => (lastEditStarted.get(field) ?? 0) > sequence;

const isInFlight = (field: string): boolean => (editsInFlight.get(field) ?? 0) > 0;

const editedSince = (field: string, sequence: number): boolean =>
  (lastEditStarted.get(field) ?? 0) >= sequence || isInFlight(field);

const isSameEdit = (a: EditContractCommand, b: EditContractCommand): boolean =>
  a.type === "EditOption" && b.type === "EditOption" ? a.option.id === b.option.id : a.type === b.type;

const removesOptionOf = (command: EditContractCommand, failed: EditContractCommand): boolean =>
  command.type === "RemoveOption" && failed.type === "EditOption" && failed.option.id === command.id;

const isValueEdit = (command: EditContractCommand): boolean => command.type.startsWith("Edit");

const isValueRefused = (result: object): boolean =>
  "error" in result && (result.error as { status?: unknown } | undefined)?.status === 400;

const refusalFieldErrors = (result: object): FieldErrorDto[] | undefined =>
  "error" in result ? (result.error as { data?: ApiErrorDto } | undefined)?.data?.fieldErrors ?? undefined : undefined;

const isMergedAgainOnResend = (command: EditContractCommand, result: object): boolean =>
  command.type === "EditOption" && !!command.lastSeen && refusalErrorCode(result) === VERSION_CONFLICT;

const refusalErrorCode = (result: object): string | undefined =>
  "error" in result ? (result.error as { data?: ApiErrorDto } | undefined)?.data?.errorCode : undefined;

const getEditRequest = (command: EditContractCommand): UpdateContractRequest => {
  switch (command.type) {
    case "EditTitle":
      return {
        title: command.value,
      };
    case "EditDescription":
      return {
        description: command.value,
      };
    case "EditCounterparty":
      return {
        counterparty: {
          counterparty: command.value ?? undefined,
        },
      };
    case "EditInternalOwner":
      return {
        internalOwner: {
          personId: command.value,
        },
      };
    case "EditTemplate":
      return {
        template: {
          templateId: command.value ?? undefined,
        },
      };
    case "EditProjects":
      return {
        editProjects: command.projects,
      };
    case "EditAwardDate":
      return {
        awardDate: {
          date: command.value,
        },
      };
    case "EditRenewalDate":
      return {
        renewalDate: {
          date: command.value,
        },
      };
    case "EditCounterpartyContactPerson":
      return {
        counterpartyContactPerson: {
          fullName: command.value.fullName ?? undefined,
          email: command.value.email ?? undefined,
          phone: command.value.phoneNumber ?? undefined,
          personId: command.value.personId ?? undefined,
        },
      };
    case "EditDeliveryTerms":
      return {
        deliveryTerms: {
          terms: command.value ?? undefined,
        },
      };
    case "EditEstimatedValue":
      return {
        valueAmount: {
          amount: command.value ?? undefined,
        },
      };
    case "EditExpirationDate":
      return {
        endDate: {
          date: command.value.date ?? undefined,
          hasNoEndDate: command.value.hasNoExpirationDate,
        },
      };
    case "EditExternalLink":
      return {
        externalLink: {
          link: command.value ?? undefined,
        },
      };
    case "EditInternalReferenceNumber":
      return {
        internalReferenceNumber: {
          number: command.value ?? undefined,
        },
      };
    case "EditLinkedContracts":
      return {
        linkedContracts: {
          contractIds: command.value ?? undefined,
        },
      };

    case "EditOriginSourcingEvent":
      return {
        originSourcingEvent: {
          eventId: command.value ?? undefined,
        },
      };
    case "EditPaymentTermsInDays":
      return {
        paymentTermsInDays: {
          days: command.value ?? undefined,
        },
      };

    case "EditPricePerMonth":
      return {
        pricePerMonth: {
          amount: command.value ?? undefined,
        },
      };

    case "EditPricePerYear":
      return {
        pricePerYear: {
          amount: command.value ?? undefined,
        },
      };

    case "EditPriority":
      return {
        priority: {
          priority: command.value ?? null,
        },
      };

    case "EditPurchasingPolicy":
      return {
        purchasingPolicy: {
          policy: command.value ?? undefined,
        },
      };

    case "EditRisk":
      return {
        risk: {
          risk: command.value ?? null,
        },
      };

    case "EditStartDate":
      return {
        startDate: {
          date: command.value,
        },
      };
    case "EditTotalLiability":
      return {
        totalLiability: {
          amount: command.value ?? undefined,
        },
      };

    case "EditTotalPrice":
      return {
        totalPrice: {
          amount: command.value ?? undefined,
        },
      };
    case "EditWarrantyExpirationDate":
      return {
        warrantyExpirationDate: {
          date: command.value,
        },
      };
    case "EditTimezone":
      return {
        timezone: {
          value: command.value,
        },
      };
    case "EditCurrency":
      return {
        currency: {
          currency: command.value,
        },
      };
    case "SendDocumentsForSigning":
      throw Error("Should never happen");
    case "AddDataFields":
      return {
        addDataFields: command.value,
      };
    case "EditCustomFields":
      return {
        editCustomFields: command.value,
      };
    case "RemoveDataField":
      return {
        removeDataField: command.value,
      };
    case "RemoveDescriptionDocument":
      return {
        removeDescriptionDocument: command.documentId,
      };
    case "AddTerminationDate":
      return {
        terminationDate: command.value,
      };
    case "EditIfrsValues":
      return {
        ifrsValues: {
          startDate: command.value.startDate ?? undefined,
          startDateSecondPeriod: command.value.startDateSecondPeriod ?? undefined,
          monthlyPayment: command.value.monthlyPayment ?? undefined,
          paymentIntervalInMonths: command.value.paymentIntervalInMonths ?? undefined,
          contractLengthInMonths: command.value.contractLengthInMonths ?? undefined,
          interestRate: command.value.interestRate ?? undefined,
        },
      };
    case "EditInternalParties":
      return {
        internalParties: command.value,
      };
    case "EditSupplierInfo":
      return {
        supplierInfo: {
          isClimateCommitted: command.value.isClimateCommitted,
          hasSignedCodeOfConduct: command.value.hasSignedCodeOfConduct,
          hasCustomerContact: command.value.hasCustomerContact,
        },
      };
    case "AddMonetaryAdjustment":
      return {
        addMonetaryValueAdjustment: {
          date: command.value.date,
          value: command.value.value,
        },
      };
    case "RemoveMonetaryAdjustment":
      return {
        removeMonetaryValueAdjustment: command.value,
      };
    case "AddPercentageAdjustment":
      return {
        addPercentageValueAdjustment: {
          date: command.value.date,
          value: command.value.value,
        },
      };
    case "RemovePercentageAdjustment":
      return {
        removePercentageValueAdjustment: command.value,
      };
    case "EditSupplierCategory":
      return {
        editSupplierCategory: { category: command.value },
      };
    case "EditNoticePeriod":
      return {
        editNoticePeriod: {
          duration: command.value,
        },
      };
    case "EditOption":
      return {
        editOption: { ...(command.option as EditOptionDto), lastSeen: command.lastSeen },
      };
    case "RemoveOption":
      return {
        removeOption: {
          id: command.id,
        },
      };
  }
};

type EditTitleCommand = {
  type: "EditTitle";
  value: string;
};

type EditDescriptionCommand = {
  type: "EditDescription";
  value: string;
};

type EditCounterpartyCommand = {
  type: "EditCounterparty";
  value: CounterpartyInputDto;
};

type EditInternalOwner = {
  type: "EditInternalOwner";
  value: string;
};

type EditTemplate = {
  type: "EditTemplate";
  value: string | null;
};

type EditAwardDate = {
  type: "EditAwardDate";
  value?: string;
};

type EditRenewalDate = {
  type: "EditRenewalDate";
  value?: string;
};

type EditCounterpartyContactPerson = {
  type: "EditCounterpartyContactPerson";
  value: {
    fullName: string | null;
    email: string | null;
    phoneNumber: PhoneNumberDto | null;
    personId: string | null;
  };
};

type EditDeliveryTerms = {
  type: "EditDeliveryTerms";
  value: string | null;
};

type EditEstimatedValue = {
  type: "EditEstimatedValue";
  value: number | null;
};

type EditExpirationDate = {
  type: "EditExpirationDate";
  value: {
    date?: string;
    hasNoExpirationDate: boolean;
  };
};

type EditExternalLink = {
  type: "EditExternalLink";
  value: string | null;
};

type EditInternalReferenceNumber = {
  type: "EditInternalReferenceNumber";
  value: string | null;
};

type EditLinkedContracts = {
  type: "EditLinkedContracts";
  value: string[];
};

type EditOriginSourcingEvent = {
  type: "EditOriginSourcingEvent";
  value: string | null;
};

type EditPaymentTermsInDays = {
  type: "EditPaymentTermsInDays";
  value: number | null;
};

type EditPricePerMonth = {
  type: "EditPricePerMonth";
  value: number | null;
};

type EditPricePerYear = {
  type: "EditPricePerYear";
  value: number | null;
};

type EditPriority = {
  type: "EditPriority";
  value: ContractPriority;
};

type EditPurchasingPolicy = {
  type: "EditPurchasingPolicy";
  value: string | null;
};

type EditRisk = {
  type: "EditRisk";
  value: ContractRisk;
};

type EditStartDate = {
  type: "EditStartDate";
  value?: string;
};

type EditTotalLiability = {
  type: "EditTotalLiability";
  value: number | null;
};

type EditTotalPrice = {
  type: "EditTotalPrice";
  value: number | null;
};

type EditWarrantyExpirationDate = {
  type: "EditWarrantyExpirationDate";
  value?: string;
};

type EditTimezone = {
  type: "EditTimezone";
  value: string;
};

type EditCurrency = {
  type: "EditCurrency";
  value: Currency;
};

export type EditIfrsValues = {
  type: "EditIfrsValues";
  value: {
    startDate?: string | null;
    startDateSecondPeriod?: string | null;
    monthlyPayment?: number | null;
    paymentIntervalInMonths?: number | null;
    contractLengthInMonths?: number | null;
    interestRate?: number | null;
  };
};

type AddTerminationDateCommand = {
  type: "AddTerminationDate";
  value: {
    date: string;
    reason: string;
  };
};

type AddDataFields = {
  type: "AddDataFields";
  value: ContractDataFieldNameDto[];
};

type EditCustomFields = {
  type: "EditCustomFields";
  value: { [sectionId: string]: ContractDataFieldSectionDto };
};

type RemoveDataField = {
  type: "RemoveDataField";
  value: ContractDataFieldNameDto;
};

type RemoveDescriptionDocument = {
  type: "RemoveDescriptionDocument";
  documentId: string;
};

type EditInternalParties = {
  type: "EditInternalParties";
  value: {
    parties: string[];
    selfRegisteredParties: string[];
  };
};

type EditSupplierInfo = {
  type: "EditSupplierInfo";
  value: {
    isClimateCommitted: boolean;
    hasSignedCodeOfConduct: boolean;
    hasCustomerContact: boolean;
  };
};

type AddMonetaryAdjustment = {
  type: "AddMonetaryAdjustment";
  value: {
    date: string;
    value: number;
  };
};

type RemoveMonetaryAdjustment = {
  type: "RemoveMonetaryAdjustment";
  value: string;
};

type AddPercentageAdjustment = {
  type: "AddPercentageAdjustment";
  value: {
    date: string;
    value: number;
  };
};

type RemovePercentageAdjustment = {
  type: "RemovePercentageAdjustment";
  value: string;
};

type SendDocumentsForSigningCommand = {
  type: "SendDocumentsForSigning";
  value: {
    mainContractDocumentId: string;
    attachmentContractDocumentIds: string[];
    signers: SignerDto[];
  };
};

type EditSupplierCategory = {
  type: "EditSupplierCategory";
  value?: Category;
};

type EditNoticePeriod = {
  type: "EditNoticePeriod";
  value: number;
};

type EditOption = {
  type: "EditOption";
  option: OptionDto;
  lastSeen?: LastSeenOption;
};

type RemoveOption = {
  type: "RemoveOption";
  id: string;
};

type EditProjects = {
  type: "EditProjects";
  projects: string[];
};

export type EditContractCommandType =
  | "EditTitle"
  | "EditDescription"
  | "EditCounterparty"
  | "EditInternalOwner"
  | "EditTemplate"
  | "EditAwardDate"
  | "EditRenewalDate"
  | "EditCounterpartyContactPerson"
  | "EditDeliveryTerms"
  | "EditEstimatedValue"
  | "EditExpirationDate"
  | "EditExternalLink"
  | "EditInternalReferenceNumber"
  | "EditLinkedContracts"
  | "EditOriginSourcingEvent"
  | "EditPaymentTermsInDays"
  | "EditPricePerMonth"
  | "EditPricePerYear"
  | "EditPriority"
  | "EditPurchasingPolicy"
  | "EditRisk"
  | "EditStartDate"
  | "EditTotalLiability"
  | "EditTotalPrice"
  | "EditWarrantyExpirationDate"
  | "EditTimezone"
  | "EditCurrency"
  | "EditIfrsValues"
  | "AddDataFields"
  | "EditCustomFields"
  | "RemoveDataField"
  | "RemoveDescriptionDocument"
  | "SendDocumentsForSigning"
  | "AddTerminationDate"
  | "EditInternalParties"
  | "EditSupplierInfo"
  | "AddMonetaryAdjustment"
  | "RemoveMonetaryAdjustment"
  | "AddPercentageAdjustment"
  | "RemovePercentageAdjustment"
  | "EditSupplierCategory"
  | "EditNoticePeriod"
  | "EditOption"
  | "RemoveOption"
  | "EditProjects";

export type EditContractCommand =
  // data fields
  | EditAwardDate
  | EditRenewalDate
  | EditCounterpartyContactPerson
  | EditDeliveryTerms
  | EditEstimatedValue
  | EditExpirationDate
  | EditExternalLink
  | EditInternalReferenceNumber
  | EditLinkedContracts
  | EditOriginSourcingEvent
  | EditPaymentTermsInDays
  | EditPricePerMonth
  | EditPricePerYear
  | EditPriority
  | EditPurchasingPolicy
  | EditRisk
  | EditStartDate
  | EditTotalLiability
  | EditTotalPrice
  | EditWarrantyExpirationDate
  | EditIfrsValues
  // other
  | RemoveDataField
  | RemoveDescriptionDocument
  | EditCurrency
  | AddDataFields
  | EditCustomFields
  | EditTimezone
  | EditCounterpartyCommand
  | SendDocumentsForSigningCommand
  | EditTitleCommand
  | EditDescriptionCommand
  | EditInternalOwner
  | EditTemplate
  | AddTerminationDateCommand
  | EditInternalParties
  | EditSupplierInfo
  | AddMonetaryAdjustment
  | RemoveMonetaryAdjustment
  | AddPercentageAdjustment
  | RemovePercentageAdjustment
  | EditSupplierCategory
  | EditNoticePeriod
  | EditOption
  | RemoveOption
  | EditProjects;
